import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./PortalDesigner.css";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Wifi, Plus, Edit, Copy, Trash2, Shield, Eye, Download, Lock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import HotspotPreview from "../components/HotspotPreview";

const PortalDesignerPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [predefinedTemplates, setPredefinedTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const [customRes, predefinedRes] = await Promise.all([
        axios.get(`${API_URL}/portals?predefined=false`, getAuthHeaders()),
        axios.get(`${API_URL}/portals?predefined=true`, getAuthHeaders())
      ]);
      setTemplates(customRes.data);
      setPredefinedTemplates(predefinedRes.data);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (templateId) => {
    try {
      await axios.post(`${API_URL}/portals/${templateId}/duplicate`, {}, getAuthHeaders());
      loadTemplates();
    } catch (error) {
      console.error("Error duplicating template:", error);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API_URL}/portals/${deleteId}`, getAuthHeaders());
      setDeleteId(null);
      loadTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
    }
  };

  const handleExport = async (templateId) => {
    try {
      const response = await axios.post(
        `${API_URL}/portals/${templateId}/export`,
        {},
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hotspot_${templateId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exporting template:", error);
      // 🔒 Mostrar mensaje de licencia
      if (error.response?.status === 403) {
        alert('🔒 Esta función es de paga.\n\nContacte al administrador para activar la descarga de portales.');
      }
    }
  };

  const TemplateCard = ({ template, isPredefined = false }) => (
    <Card className="template-card hover:shadow-lg transition-shadow" data-testid={`template-card-${template.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl mb-2" data-testid={`template-name-${template.id}`}>{template.name}</CardTitle>
            <CardDescription data-testid={`template-description-${template.id}`}>{template.description || "Sin descripción"}</CardDescription>
          </div>
          {template.sslConfig?.enabled && (
            <Badge variant="secondary" className="ml-2" data-testid={`ssl-badge-${template.id}`}>
              <Shield className="w-3 h-3 mr-1" />
              SSL
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex gap-2">
            <div 
              className="w-8 h-8 rounded-full border-2" 
              style={{ backgroundColor: template.design?.primaryColor || '#3b82f6' }}
              data-testid={`primary-color-${template.id}`}
            />
            <div 
              className="w-8 h-8 rounded-full border-2" 
              style={{ backgroundColor: template.design?.secondaryColor || '#8b5cf6' }}
              data-testid={`secondary-color-${template.id}`}
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Wifi className="w-4 h-4" />
            <span data-testid={`login-method-${template.id}`}>
              {template.loginConfig?.method === 'pin' && 'Login por PIN'}
              {template.loginConfig?.method === 'userpass' && 'Usuario y Contraseña'}
              {template.loginConfig?.method === 'qr' && 'Código QR'}
              {template.loginConfig?.method === 'social' && 'Redes Sociales'}
              {template.loginConfig?.method === 'free' && 'Acceso Libre'}
              {template.loginConfig?.method === 'multiple' && 'Múltiples Métodos'}
            </span>
          </div>
          {template.pricing?.length > 0 && (
            <div className="text-sm text-gray-600" data-testid={`pricing-count-${template.id}`}>
              {template.pricing.length} plan(es) de precios
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        {isPredefined ? (
          <>
            <Button 
              onClick={() => setPreviewTemplate(template)} 
              variant="outline"
              className="flex-1"
              data-testid={`preview-btn-${template.id}`}
            >
              <Eye className="w-4 h-4 mr-2" />
              Vista Previa
            </Button>
            <Button 
              onClick={() => handleDuplicate(template.id)} 
              className="flex-1"
              data-testid={`duplicate-btn-${template.id}`}
            >
              <Copy className="w-4 h-4 mr-2" />
              Usar
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={() => setPreviewTemplate(template)} 
              variant="outline"
              size="sm"
              data-testid={`preview-btn-${template.id}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button 
              onClick={() => navigate(`/portal-designer/editor/${template.id}`)} 
              size="sm"
              data-testid={`edit-btn-${template.id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button 
              onClick={() => handleExport(template.id)} 
              variant="outline"
              size="sm"
              className="bg-green-50 hover:bg-green-100 border-green-200"
              data-testid={`export-btn-${template.id}`}
              title="Requiere licencia"
            >
              <Download className="w-4 h-4 mr-1" />
              <Lock className="w-3 h-3" />
            </Button>
            <Button 
              onClick={() => handleDuplicate(template.id)} 
              variant="outline"
              size="sm"
              data-testid={`copy-btn-${template.id}`}
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button 
              onClick={() => setDeleteId(template.id)} 
              variant="destructive"
              size="sm"
              data-testid={`delete-btn-${template.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );

  return (
    <div className="portal-designer-light min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 fade-in">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                <span className="ml-2">Volver</span>
              </Button>
            </div>
            <Button 
              onClick={() => navigate('/portal-designer/editor')} 
              size="lg" 
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              data-testid="create-template-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nueva Plantilla
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="predefined" className="fade-in">
          <TabsList className="mb-6" data-testid="template-tabs">
            <TabsTrigger value="custom" data-testid="custom-tab">
              Mis Plantillas ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="predefined" data-testid="predefined-tab">
              Plantillas Prediseñadas ({predefinedTemplates.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="custom" data-testid="custom-templates-content">
            {loading ? (
              <div className="flex justify-center items-center py-20" data-testid="loading-spinner">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : templates.length === 0 ? (
              <Card className="p-12 text-center" data-testid="no-custom-templates">
                <Wifi className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold mb-2">No tienes plantillas personalizadas</h3>
                <p className="text-gray-600 mb-4">Crea una nueva o usa una plantilla prediseñada</p>
                <Button onClick={() => navigate('/portal-designer/editor')} data-testid="create-first-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Primera Plantilla
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="custom-templates-grid">
                {templates.map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="predefined" data-testid="predefined-templates-content">
            {loading ? (
              <div className="flex justify-center items-center py-20" data-testid="predefined-loading-spinner">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="predefined-templates-grid">
                {predefinedTemplates.map((template) => (
                  <TemplateCard key={template.id} template={template} isPredefined />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle data-testid="preview-dialog-title">
              Vista Previa: {previewTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="mt-4">
              <HotspotPreview template={previewTemplate} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="delete-dialog-title">¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription data-testid="delete-dialog-description">
              Esta acción no se puede deshacer. La plantilla será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortalDesignerPage;
